import { Logger } from '../support/Logger';

/**
 * rrweb (Mixpanel Session Replay) records DOM/inline-style mutations but NOT
 * Web Animations API state. Ionic presents every overlay via element.animate()
 * with fill 'both', so the overlay's visible end state (opacity 1, transform
 * none) lives only in the running animation and is never written to inline
 * style -- in a replay the overlay stays at its pre-animation state and looks
 * invisible. This patches Element.prototype.animate to commitStyles() the
 * end state once a filling animation finishes, so rrweb records it.
 *
 * Within a single present cycle this is visually a no-op for live users
 * (commitStyles persists exactly what the finished animation already shows).
 * Ionic reuses overlay DOM across present/dismiss, so a committed inline
 * opacity/transform persists onto the next present; that is re-established by
 * Ionic's beforeStyles + keyframe-0 + WAAPI compositing, so it is not expected
 * to regress, but the multi-present path is the one to smoke-test.
 *
 * Patching the prototype (vs per-overlay enter/leaveAnimation props) is
 * deliberate: overlays open from dozens of call sites and Ionic exposes no
 * global default-animation hook, so a per-overlay approach would silently miss
 * any overlay that didn't opt in.
 */
export class WebAnimationStyleCommitter {
    static inject = () => [Logger];
    constructor(private log: Logger) {}

    private installed = false;

    install = () => {
        if (this.installed) {
            return;
        }
        const proto = typeof Element !== 'undefined' ? Element.prototype : undefined;
        if (!proto || typeof proto.animate !== 'function') {
            return;
        }
        this.installed = true;

        const originalAnimate = proto.animate;
        const scheduleCommit = this.scheduleCommit;

        proto.animate = function (
            this: Element,
            keyframes: Keyframe[] | PropertyIndexedKeyframes | null,
            options?: number | KeyframeAnimationOptions
        ): Animation {
            const animation = originalAnimate.call(this, keyframes, options);
            scheduleCommit(animation);
            return animation;
        };

        this.log.info('WebAnimationStyleCommitter: installed');
    };

    // Synchronous gate so the common non-filling animation (every ripple, route
    // transition, spinner) returns here without entering the async path or
    // allocating a Promise. fill forwards/both both targets Ionic overlay
    // enters and skips infinite spinners, whose `finished` never resolves.
    private scheduleCommit = (animation: Animation): void => {
        const fill = animation.effect?.getComputedTiming().fill;
        if (fill !== 'forwards' && fill !== 'both') {
            return;
        }
        if (typeof animation.commitStyles !== 'function') {
            return;
        }
        void this.commitAfterFinished(animation);
    };

    private commitAfterFinished = async (animation: Animation): Promise<void> => {
        try {
            await animation.finished;
            animation.commitStyles();
        } catch {
            // finished rejects when the animation is cancelled, and
            // commitStyles throws if the target is no longer committable --
            // both happen routinely when an overlay is dismissed mid-enter.
            // There is no end state to persist; expected and not actionable.
        }
    };
}
